import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from "@angular/core";
import {BokkzIbDataService, IncomeTaxEntity, OmschrijvingEnBedrag} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormArray, FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  shareReplay,
  Subscription,
  switchMap,
  take,
  tap
} from "rxjs";
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

type OmschrijvingBedragFormGroup = FormGroup<{
  omschrijving: FormControl<string | null>;
  bedrag: FormControl<number | null>;
}>;

@Component({
  selector: 'snelstart-venture-fiscaal-feature-huurinkomsten',
  templateUrl: './huurinkomsten.component.html',
  styleUrls: ['./huurinkomsten.component.scss'],
  imports: [
    SharedUiButtonModule,
    ReactiveFormsModule,
    SharedUiDetailsModule,
    SharedUiFormControlsModule,
    CommonModule
  ]
})
export class HuurinkomstenComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  private changeSubscriptions = new Subscription();
  public dialogVisible = false;

  public formGroup = new FormGroup({
    huur: new FormArray<OmschrijvingBedragFormGroup>([]),
    kosten: new FormArray<OmschrijvingBedragFormGroup>([]),
  });

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> =
    this.dataService.incomeTaxEntity$.pipe(
      filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  public ngOnInit(): void {
    this.incomeTaxEntity$.pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((data) => {
        this.updateForm(data.aangifte?.box1?.belastbareInkomstenUitEigenWoning?.huurinkomstenEigenWoningen || [], this.huur);
        this.updateForm(data.aangifte?.box1?.belastbareInkomstenUitEigenWoning?.kostenVerhuurEigenWoningen || [], this.kosten);
      });
    this.subscribeToChanges();
  }

  ngOnDestroy(): void {
    this.changeSubscriptions.unsubscribe();
  }

  private subscribeToChanges(): void {
    this.changeSubscriptions.unsubscribe();
    this.changeSubscriptions = new Subscription();

    const extractRelevant = () => this.huur.getRawValue().map(w => ({
      bedrag: w.bedrag,
    }));

    this.changeSubscriptions.add(
      this.huur.valueChanges.pipe(
        debounceTime(500),
        tap(() => this.saveForm()),
        map(() => extractRelevant()),
        distinctUntilChanged((prev, curr) =>
          JSON.stringify(prev) === JSON.stringify(curr)
        ),
        switchMap(() => this.dataService.calculateLatestIncomeTaxReturn()),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe()
    );
  }

  get huur(): FormArray<OmschrijvingBedragFormGroup> {
    return this.formGroup.controls.huur;
  }

  get kosten(): FormArray<OmschrijvingBedragFormGroup> {
    return this.formGroup.controls.kosten;
  }

  addRijHuur(): void {
    this.huur.push(this.createFormGroup())
  }

  addRijKosten(): void {
    this.kosten.push(this.createFormGroup())
  }

  verwijderRijHuur(index: number): void {
    this.huur.removeAt(index, {emitEvent: false});
    this.formGroup.updateValueAndValidity({emitEvent: false})
    this.formGroup.markAsDirty({emitEvent: false})
    this.saveForm();
  }

  verwijderRijKosten(index: number): void {
    this.kosten.removeAt(index, {emitEvent: false});
    this.formGroup.updateValueAndValidity({emitEvent: false})
    this.formGroup.markAsDirty({emitEvent: false})
    this.saveForm();
  }

  private createFormGroup(item?: OmschrijvingEnBedrag): OmschrijvingBedragFormGroup {
    return new FormGroup({
      omschrijving: new FormControl<string | null>(item?.omschrijving ?? null),
      bedrag: new FormControl<number | null>(item?.bedrag ?? null),
    });
  }

  private updateForm(data : OmschrijvingEnBedrag[], array: FormArray): void {
    const lijst = data || [];
    const formArray = array;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }
    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i) as FormGroup;
        control.patchValue(lijst[i], {emitEvent: false});
      } else {
        const formGroup = this.createFormGroup(lijst[i]);
        formArray.push(formGroup, {emitEvent: false});
      }
    }
  }

  saveForm(): void {
    const opbrengsten: OmschrijvingEnBedrag[] = this.huur.getRawValue()
      .filter(item => item.omschrijving || item.bedrag !== null)
      .map(item => ({
        omschrijving: item.omschrijving ?? undefined,
        bedrag: item.bedrag ?? undefined,
      }));

    const kosten: OmschrijvingEnBedrag[] = this.kosten.getRawValue()
      .filter(item => item.omschrijving || item.bedrag !== null)
      .map(item => ({
        omschrijving: item.omschrijving ?? undefined,
        bedrag: item.bedrag ?? undefined,
      }));

    this.dataService.patchIncomeTaxEntity(currentEntity => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      return {
        ...currentEntity,
        aangifte: {
          ...currentEntity.aangifte,
          box1: {
            ...currentEntity.aangifte.box1,
            belastbareInkomstenUitEigenWoning: {
              ...currentEntity.aangifte.box1?.belastbareInkomstenUitEigenWoning,
              huurinkomstenEigenWoningen: opbrengsten,
              kostenVerhuurEigenWoningen: kosten
            }
          }
        },
      } as IncomeTaxEntity;
    });
  }
}
